import { writeFile, readFile, mkdir } from 'node:fs/promises';
import { join } from 'node:path';
import chalk from 'chalk';
import ora from 'ora';
import { loadConfig, getDefaultConfig } from '../../engine/config-loader.js';

interface FixOptions {
  dryRun?: boolean;
  all?: boolean;
}

interface FixAction {
  file: string;
  ruleId: string;
  description: string;
  safe: boolean;
  action: () => Promise<void>;
}

export async function fixCommand(options: FixOptions): Promise<void> {
  const cwd = process.cwd();
  const spinner = ora('Analyzing project for fixes...').start();

  try {
    const fixes: FixAction[] = [];

    // Collect all available fixes
    spinner.text = 'Checking .gitignore...';
    fixes.push(...(await getGitignoreFixes(cwd)));

    spinner.text = 'Checking .editorconfig...';
    fixes.push(...(await getEditorConfigFixes(cwd)));

    spinner.text = 'Checking tsconfig.json...';
    fixes.push(...(await getTsConfigFixes(cwd)));

    spinner.text = 'Checking Node version...';
    fixes.push(...(await getNodeVersionFixes(cwd)));

    spinner.text = 'Checking security headers...';
    fixes.push(...(await getSecurityFixes(cwd)));

    spinner.stop();

    // Filter to safe fixes only unless --all
    const applicableFixes = options.all ? fixes : fixes.filter((f) => f.safe);

    if (applicableFixes.length === 0) {
      console.log(chalk.green('\n✓ No auto-fixable issues found!\n'));
      return;
    }

    console.log(chalk.bold(`\n🔧 Found ${applicableFixes.length} fix(es):\n`));

    for (const fix of applicableFixes) {
      const safeLabel = fix.safe ? chalk.green('[safe]') : chalk.yellow('[review]');
      console.log(`  ${safeLabel} ${chalk.cyan(fix.file)}`);
      console.log(`    ${chalk.dim(fix.ruleId)}: ${fix.description}`);
    }

    if (options.dryRun) {
      console.log(chalk.yellow('\n[Dry run] No changes applied.'));
      console.log(chalk.dim('Run without --dry-run to apply fixes.\n'));
      return;
    }

    console.log();
    const applySpinner = ora('Applying fixes...').start();

    let applied = 0;
    for (const fix of applicableFixes) {
      applySpinner.text = `Fixing ${fix.file}...`;
      try {
        await fix.action();
        applied++;
      } catch (error) {
        // Log but continue
        console.error(chalk.red(`Failed to fix ${fix.file}: ${error}`));
      }
    }

    applySpinner.succeed(chalk.green(`Applied ${applied}/${applicableFixes.length} fix(es)`));

    console.log(chalk.bold('\n🚀 Next steps:\n'));
    console.log(`  1. ${chalk.cyan('git diff')}        Review the changes`);
    console.log(`  2. ${chalk.cyan('devrail check')}   Verify fixes worked`);
    console.log(`  3. ${chalk.cyan('git commit')}      Commit the fixes\n`);
  } catch (error) {
    spinner.fail(chalk.red('Fix failed'));
    console.error(error);
    process.exit(1);
  }
}

async function getGitignoreFixes(cwd: string): Promise<FixAction[]> {
  const fixes: FixAction[] = [];
  const gitignorePath = join(cwd, '.gitignore');

  const requiredPatterns = [
    '# Environment',
    '.env',
    '.env.local',
    '.env*.local',
    '',
    '# Dependencies',
    'node_modules/',
    '',
    '# Build',
    'dist/',
    'build/',
    '.next/',
    '',
    '# Devrail',
    '.devrail/',
  ];

  try {
    const content = await readFile(gitignorePath, 'utf-8');
    const missingPatterns = requiredPatterns.filter(
      (p) => p && !content.includes(p.replace('/', ''))
    );

    if (missingPatterns.length > 3) {
      fixes.push({
        file: '.gitignore',
        ruleId: 'secrets.gitignore-required',
        description: 'Add missing patterns for env files and build artifacts',
        safe: true,
        action: async () => {
          const newContent = content.trimEnd() + '\n\n' + missingPatterns.join('\n') + '\n';
          await writeFile(gitignorePath, newContent, 'utf-8');
        },
      });
    }
  } catch {
    fixes.push({
      file: '.gitignore',
      ruleId: 'secrets.gitignore-required',
      description: 'Create .gitignore with security patterns',
      safe: true,
      action: async () => {
        await writeFile(gitignorePath, requiredPatterns.join('\n') + '\n', 'utf-8');
      },
    });
  }

  return fixes;
}

async function getEditorConfigFixes(cwd: string): Promise<FixAction[]> {
  const fixes: FixAction[] = [];
  const editorConfigPath = join(cwd, '.editorconfig');

  const defaultConfig = `# EditorConfig - https://editorconfig.org
root = true

[*]
indent_style = space
indent_size = 2
end_of_line = lf
charset = utf-8
trim_trailing_whitespace = true
insert_final_newline = true

[*.md]
trim_trailing_whitespace = false
`;

  try {
    await readFile(editorConfigPath, 'utf-8');
  } catch {
    fixes.push({
      file: '.editorconfig',
      ruleId: 'config.editor-config',
      description: 'Create .editorconfig for consistent formatting',
      safe: true,
      action: async () => {
        await writeFile(editorConfigPath, defaultConfig, 'utf-8');
      },
    });
  }

  return fixes;
}

async function getTsConfigFixes(cwd: string): Promise<FixAction[]> {
  const fixes: FixAction[] = [];
  const tsconfigPath = join(cwd, 'tsconfig.json');

  try {
    const content = await readFile(tsconfigPath, 'utf-8');
    const tsconfig = JSON.parse(content);

    if (!tsconfig.compilerOptions?.strict) {
      fixes.push({
        file: 'tsconfig.json',
        ruleId: 'code.strict-mode',
        description: 'Enable TypeScript strict mode',
        safe: false, // May break existing code
        action: async () => {
          tsconfig.compilerOptions = tsconfig.compilerOptions || {};
          tsconfig.compilerOptions.strict = true;
          await writeFile(tsconfigPath, JSON.stringify(tsconfig, null, 2) + '\n', 'utf-8');
        },
      });
    }
  } catch {
    // No tsconfig, skip
  }

  return fixes;
}

async function getNodeVersionFixes(cwd: string): Promise<FixAction[]> {
  const fixes: FixAction[] = [];
  const nvmrcPath = join(cwd, '.nvmrc');

  try {
    await readFile(nvmrcPath, 'utf-8');
  } catch {
    try {
      const pkgPath = join(cwd, 'package.json');
      const pkgContent = await readFile(pkgPath, 'utf-8');
      const pkg = JSON.parse(pkgContent);

      if (!pkg.engines?.node) {
        fixes.push({
          file: '.nvmrc',
          ruleId: 'config.node-version',
          description: 'Create .nvmrc with Node.js LTS version',
          safe: true,
          action: async () => {
            await writeFile(nvmrcPath, '20\n', 'utf-8');
          },
        });
      }
    } catch {
      // No package.json, skip
    }
  }

  return fixes;
}

async function getSecurityFixes(cwd: string): Promise<FixAction[]> {
  const fixes: FixAction[] = [];

  // Check for Next.js security headers
  const nextConfigPath = join(cwd, 'next.config.js');
  const nextConfigMjsPath = join(cwd, 'next.config.mjs');

  try {
    let configPath = nextConfigPath;
    let content: string;
    try {
      content = await readFile(nextConfigPath, 'utf-8');
    } catch {
      content = await readFile(nextConfigMjsPath, 'utf-8');
      configPath = nextConfigMjsPath;
    }

    if (!content.includes('headers')) {
      fixes.push({
        file: configPath.split('/').pop() ?? 'next.config.js',
        ruleId: 'security.headers.required',
        description: 'Add security headers configuration (manual review needed)',
        safe: false,
        action: async () => {
          // This is a complex fix, just create a comment/TODO
          const headerComment = `
// TODO: Add security headers
// See: https://nextjs.org/docs/app/api-reference/next-config-js/headers
// async headers() {
//   return [
//     {
//       source: '/:path*',
//       headers: [
//         { key: 'X-Frame-Options', value: 'DENY' },
//         { key: 'X-Content-Type-Options', value: 'nosniff' },
//         { key: 'Referrer-Policy', value: 'strict-origin-when-cross-origin' },
//       ],
//     },
//   ];
// },
`;
          const newContent = content.replace(
            'module.exports',
            headerComment + '\nmodule.exports'
          );
          await writeFile(configPath, newContent, 'utf-8');
        },
      });
    }
  } catch {
    // Not a Next.js project or no config
  }

  return fixes;
}
